import { useEffect, useMemo, useState } from 'react';
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { useDispatch } from 'react-redux';
import { updatePermissions, removeAdmin } from '../../../store/actions/admin/bl-admin';
import { BLAdmin, ChannelPermission, PERMISSIONS } from '../../../types/working-model';
import { capitalize, userHasRoleOnActiveChannel } from '../../../utils/utils';
import Icon, { COLORS, ICONS } from '../../general-ui/icon';
import Switch from '../../general-ui/switch/switch';
import { Popover } from '../../general-ui/popover/popover';
import ButtonPlain from "../../general-ui/button-plain/button-plain";
import { setWorkingPermissions } from '../../../store/actions/admin/bl-admin';

interface Props {
	profile: BLAdmin;
}

export default function AccessRow(props: Props): JSX.Element {
	const { profile } = props;

	const { token, user } = useTypedSelector(state => state.AuthReducer);
	const { admins, workingPermissions } = useTypedSelector(state => state.ChannelAdminReducer);

	const [openAccess, setOpenAccess] = useState(false);
	const [profileToUpdate, setProfileToUpdate] = useState<BLAdmin | null>(null);
	const [remove, setRemove] = useState<boolean>(false);

	const dispatch = useDispatch();

	const accessCredentials = useMemo(() => {
		return profile.channels[user!.active_channel] || [];
	}, [profile.channels, user]);

	const handleToggle = (toggle: string): void => {
		let credentials: string[] = [...accessCredentials];
		const index: number = credentials.indexOf(toggle);

		if (index > -1) {
			toggle === PERMISSIONS.Owner
				// when toggle owner off, demote to admin
				? credentials = [PERMISSIONS.Admin]
				// all others simply turn off
				: credentials.splice(index, 1);
		} else {
			toggle === PERMISSIONS.Analytics || toggle === PERMISSIONS.Moderation || PERMISSIONS.Producer
				// analytics and moderation may both be on, admin and owner are off
				? credentials = credentials.filter(permission => permission !== PERMISSIONS.Owner && permission !== PERMISSIONS.Admin).concat(toggle)
				// if admin or owner is on, all others are off
				: credentials = [toggle];
		}

		if (user) {
			const newProfile: BLAdmin = {
				...profile,
				channels: {
					...profile.channels,
					[user.active_channel]: credentials,
				}
			};
			setProfileToUpdate(newProfile);
		}

		// only one owner per channel - if owner toggled on, previous owner(s) demoted to admin
		if (toggle === PERMISSIONS.Owner && index === -1) {
			user && admins.map(admin => {
				if (admin.channels[user.active_channel].includes(PERMISSIONS.Owner) && admin.id !== profile.id) {
					const demoteAdmin: BLAdmin = {
						...admin,
						channels: {
							...admin.channels,
							[user.active_channel]: [PERMISSIONS.Admin],
						}
					};
					dispatch(updatePermissions(token!, demoteAdmin));
					if (admin.id === user.id) {
						dispatch(setWorkingPermissions(user.channels[user?.active_channel]));
					}
				}
			});
		}
	};

	useEffect(() => {
		if (profileToUpdate) { dispatch(updatePermissions(token!, profileToUpdate)); }
	}, [dispatch, profileToUpdate, token]);

	const formattedCredentials = useMemo(() => {
		return accessCredentials.map((cred: string) => {
			return capitalize(cred);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessCredentials, admins]);

	const handleRemoveAdmin = (): void => {
		if (user) {
			const newChannels: ChannelPermission = { ...profile.channels };
			delete newChannels[user.active_channel];
			dispatch(removeAdmin(token!, { ...profile, channels: newChannels }));
		}
	};

	return (
		<div className="access-row">
			<div className="access-row-left">
				<div className="access-icon">
					{profile.profile.picture ? (
						<img src={profile.profile.picture} />
					) : (
						<></>
					)}
				</div>
				<div className="access-info">
					<div className="access-email">{profile.email}</div>
				</div>
			</div>
			<div className="access-list">
				<button
					className="no-style list-button"
					disabled={
						// must be an admin or owner to open panel
						!userHasRoleOnActiveChannel([PERMISSIONS.Owner, PERMISSIONS.Admin], user!)
						// and only owner can open owner's panel
						|| profile.channels[user!.active_channel]?.includes(PERMISSIONS.Owner) && !userHasRoleOnActiveChannel([PERMISSIONS.Owner], user!)
					}
					onClick={() => setOpenAccess(!openAccess)}
				>
					{formattedCredentials.join(', ')}
					<Icon
						name={
							openAccess
								? ICONS.KEYBOARD_ARROW_RIGHT
								: ICONS.KEYBOARD_ARROW_DOWN
						}
						size={16}
						color={COLORS.DEFAULT_GRAY}
					/>
				</button>
				<div className={`access-popover ${openAccess ? "open" : ""}`}>
					<Popover open={openAccess}>
						{!remove ? (
							<ul>
								<li>
									<div className="access-title">Owner</div>
									<div className="access-toggle">
										<Switch
											value={'owner-toggle'}
											onClick={() => handleToggle(PERMISSIONS.Owner)}
											on={accessCredentials.includes(PERMISSIONS.Owner)}
											// only channel owner can change ownership
											disabled={!workingPermissions?.includes(PERMISSIONS.Owner)}
										/>
									</div>
								</li>
								<li>
									<div className="access-title">Admin</div>
									<div className="access-toggle">
										<Switch
											value={'admin-toggle'}
											onClick={() => handleToggle(PERMISSIONS.Admin)}
											on={accessCredentials.includes(PERMISSIONS.Admin)}
										/>
									</div>
								</li>
								<li>
									<div className="access-title">Analytics Only</div>
									<div className="access-toggle">
										<Switch
											value={'analytics-toggle'}
											onClick={() => handleToggle(PERMISSIONS.Analytics)}
											on={accessCredentials.includes(PERMISSIONS.Analytics)}
										/>
									</div>
								</li>
								<li>
									<div className="access-title">Moderation Only</div>
									<div className="access-toggle">
										<Switch
											value={'moderation-toggle'}
											onClick={() => handleToggle(PERMISSIONS.Moderation)}
											on={accessCredentials.includes(PERMISSIONS.Moderation)}
										/>
									</div>
								</li>
								<li>
									<div className="access-title">Producer Only</div>
									<div className="access-toggle">
										<Switch
											value={'moderation-toggle'}
											onClick={() => handleToggle(PERMISSIONS.Producer)}
											on={accessCredentials.includes(PERMISSIONS.Producer)}
										/>
									</div>
								</li>
								{!workingPermissions?.includes(PERMISSIONS.Owner) && <li>
									<ButtonPlain
										className={"remove-button"}
										icon={ICONS.TRASH}
										iconSize={14}
										color={COLORS.WHITE}
										style={{ color: COLORS.WHITE }}
										onclick={() => setRemove(true)}
									>
										Remove admin
									</ButtonPlain>
								</li>}
							</ul>
						) : (
							<div className="remove-confirm">
								Confirm removal of {profile.email}?
								<div className="confirm-buttons">
									<button className="small" onClick={() => setRemove(false)} style={{ marginRight: 16 }}>
										Cancel
									</button>
									<button className="small primary" onClick={handleRemoveAdmin} >
										Remove
									</button>
								</div>
							</div>

						)}
					</Popover>
				</div>
			</div>
		</div>
	);
}
